import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * SidebarService manages the sidebar collapsed/expanded state with localStorage persistence.
 * 
 * Features:
 * - BehaviorSubject for reactive state management
 * - localStorage persistence for user preferences
 * - Error handling for localStorage failures (private browsing, quota exceeded, etc.)
 * - Observable stream for components to subscribe to state changes
 * 
 * Requirements: 6.6, 6.7, 6.8, 6.9
 */
@Injectable({ providedIn: 'root' })
export class SidebarService {
  private readonly STORAGE_KEY = 'sidebar-collapsed';
  private collapsedSubject = new BehaviorSubject<boolean>(this.getInitialState());
  public collapsed$: Observable<boolean> = this.collapsedSubject.asObservable();

  constructor() {}

  /**
   * Toggle the sidebar collapsed state between expanded and collapsed.
   * Persists the new state to localStorage.
   * 
   * Requirement 6.6: Support collapsible behavior
   */
  toggle(): void {
    const newState = !this.collapsedSubject.value;
    this.collapsedSubject.next(newState);
    this.persistState(newState);
  }

  /**
   * Set the sidebar collapsed state explicitly.
   * Persists the new state to localStorage.
   * 
   * @param collapsed - true to collapse the sidebar, false to expand it
   * 
   * Requirement 6.6: Support collapsible behavior
   * Requirement 6.7: Display only icons when collapsed
   * Requirement 6.8: Display icons with text labels when expanded
   */
  setCollapsed(collapsed: boolean): void {
    this.collapsedSubject.next(collapsed);
    this.persistState(collapsed);
  }

  /**
   * Get the current collapsed state value synchronously.
   * 
   * @returns true if sidebar is collapsed, false if expanded
   */
  isCollapsed(): boolean {
    return this.collapsedSubject.value;
  }

  /**
   * Persist the collapsed state to localStorage with error handling.
   * Handles common localStorage failures gracefully:
   * - Private browsing mode (localStorage unavailable)
   * - Quota exceeded errors
   * - Security errors
   * 
   * @param collapsed - the state to persist
   * 
   * Requirement 6.9: Persist collapsed/expanded state in localStorage
   */
  private persistState(collapsed: boolean): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(collapsed));
    } catch (error) {
      // Handle localStorage errors gracefully
      if (error instanceof DOMException) {
        // QuotaExceededError
        if (error.name === 'QuotaExceededError') {
          console.warn('SidebarService: localStorage quota exceeded. Sidebar state will not persist.');
        }
        // SecurityError (private browsing, cross-origin, etc.)
        else if (error.name === 'SecurityError') {
          console.warn('SidebarService: localStorage access denied. Sidebar state will not persist.');
        }
        // Other DOMException errors
        else {
          console.warn('SidebarService: localStorage error:', error.message);
        }
      } else {
        // Unexpected error
        console.error('SidebarService: Unexpected error persisting state:', error);
      }
      // Continue execution - the app should work even if persistence fails
    }
  }

  /**
   * Get the initial collapsed state from localStorage or default to false (expanded).
   * Handles localStorage read errors gracefully.
   * 
   * @returns the initial collapsed state
   * 
   * Requirement 6.9: Persist collapsed/expanded state in localStorage
   */
  private getInitialState(): boolean {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored !== null) {
        return JSON.parse(stored);
      }
    } catch (error) {
      // Handle localStorage read errors or JSON parse errors
      if (error instanceof DOMException) {
        console.warn('SidebarService: localStorage read error. Using default state (expanded).');
      } else if (error instanceof SyntaxError) {
        console.warn('SidebarService: Invalid stored state. Using default state (expanded).');
      } else {
        console.error('SidebarService: Unexpected error reading initial state:', error);
      }
    }
    
    // Default to expanded (false) if no stored value or error occurred
    return false;
  }
}
